/*
 * PRD-037 — Modal de contratación del paso de plan del claim (app SEPARADA).
 *
 * En el claim el ÚNICO método de pago ofrecido es TARJETA (la transferencia se oculta a propósito:
 * el dueño recién activado contrata en línea con tarjeta + 3DS). Por eso NO se monta el
 * PaymentMethodSelector compartido del panel.
 *
 * Sin paso de "confirmar método": mostrar una tarjeta que dice "Tarjeta de crédito o débito" como
 * si fuera una elección, cuando es la ÚNICA opción, no aporta nada — el clic en "Elegir <plan>"
 * (ClaimPlanStep) YA es la decisión deliberada del dueño. Al abrir el modal se dispara
 * convertFreemiumToPlan de una vez y se muestra el formulario de tarjeta (Stripe Elements) en
 * cuanto llega el client_secret; mientras tanto un estado de carga explícito.
 *
 * Reusa los presentacionales del panel (StripePaymentForm / isStripeConfigured) sobre
 * convertFreemiumToPlan (axiosInstance directo). Los errores se muestran inline (el partner no
 * monta <Toaster>).
 */
import { useEffect, useRef, useState } from 'react';
import { Modal } from '@plick/ui';
import { IconLock } from '@tabler/icons-react';
import { V4 } from '@components/v4/tokens';
import { Button } from '@components/v4/Button/Button';
import { StripePaymentForm, isStripeConfigured } from '@pages/Subscriptionsv2/components/StripePaymentForm';
import type { MetodoPago } from '@pages/Subscriptionsv2/hooks/useActivatePlan';
import type { AvailableSubscription } from '@interfaces/general';
import { formatToCurrency } from '@utils/formats';
import { convertFreemiumToPlan } from './useClaimPlans';

// En el claim el método de pago es siempre tarjeta (la transferencia no se ofrece).
const METODO_PAGO: MetodoPago = 'tarjeta';

interface ClaimPlanModalProps {
  plan: AvailableSubscription;
  isOpen: boolean;
  onClose: () => void;
  /** Se invoca tras una contratación exitosa (tarjeta confirmada / 3DS resuelto). */
  onActivated: () => void;
}

export const ClaimPlanModal = ({ plan, isOpen, onClose, onActivated }: ClaimPlanModalProps) => {
  const [clientSecret, setClientSecret] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Evita re-disparar convertFreemiumToPlan (crea la suscripción incomplete en Stripe) más de una
  // vez por apertura del modal — guard síncrono, no depende del estado async.
  const fetchedRef = useRef(false);

  const cardConfigured = isStripeConfigured();

  const fetchClientSecret = () => {
    if (fetchedRef.current || !cardConfigured) return;
    fetchedRef.current = true;
    setError(null);
    setIsLoading(true);
    convertFreemiumToPlan(plan.id, METODO_PAGO)
      .then((response) => {
        if (response.client_secret) {
          setClientSecret(response.client_secret);
        } else {
          setError('No se recibió la información de pago. Intenta de nuevo.');
        }
      })
      .catch((err: unknown) => {
        const message =
          (err as { response?: { data?: { message?: string } } })?.response?.data?.message ||
          (err as Error)?.message ||
          'No se pudo activar el plan. Intenta de nuevo.';
        setError(message);
      })
      .finally(() => setIsLoading(false));
  };

  // Al abrir el modal (o reintentar tras un error) pide el client_secret de una vez —
  // sin paso intermedio de "confirmar método".
  useEffect(() => {
    if (!isOpen) {
      fetchedRef.current = false;
      setClientSecret(null);
      setError(null);
      return;
    }
    fetchClientSecret();
  }, [isOpen, plan.id]);

  const handleClose = () => {
    setClientSecret(null);
    setError(null);
    onClose();
  };

  const handleRetry = () => {
    fetchedRef.current = false;
    fetchClientSecret();
  };

  return (
    <Modal padding="0rem" maxHeight="90vh" maxWidth="480px" isOpen={isOpen} onClose={handleClose}>
      <div style={{ padding: 24, fontFamily: 'Poppins, sans-serif' }}>
        <h3 style={{ margin: 0, fontSize: 18, fontWeight: 700, color: V4.primary }}>Activar {plan.nombre}</h3>
        {typeof plan.precio === 'number' && (
          <p style={{ margin: '4px 0 16px', fontSize: 13, color: V4.grayUltraDark }}>
            {formatToCurrency(plan.precio)} · {plan.periodo_pago === 'anual' ? 'pago anual' : 'pago mensual'}
          </p>
        )}

        {!cardConfigured && (
          <p style={{ margin: 0, fontSize: 12, color: V4.warningDark, lineHeight: 1.4 }}>
            El pago con tarjeta no está disponible por ahora. Vuelve a intentarlo más tarde o contacta a soporte.
          </p>
        )}

        {cardConfigured && isLoading && !clientSecret && (
          <p style={{ margin: 0, fontSize: 13, color: V4.grayUltraDark }}>Preparando tu pago…</p>
        )}

        {cardConfigured && error && !isLoading && (
          <>
            <p style={{ margin: '0 0 16px', fontSize: 12, color: V4.danger, lineHeight: 1.4 }} role="alert">
              {error}
            </p>
            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
              <Button variant="secondary" onClick={handleClose}>
                Cancelar
              </Button>
              <Button variant="primary" onClick={handleRetry}>
                Reintentar
              </Button>
            </div>
          </>
        )}

        {cardConfigured && clientSecret && (
          <>
            <p
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 6,
                margin: '0 0 16px',
                fontSize: 12,
                color: V4.grayUltraDark,
              }}
            >
              <IconLock size={14} stroke={1.8} />
              Pago seguro procesado por Stripe.
            </p>
            <StripePaymentForm clientSecret={clientSecret} onSuccess={onActivated} onCancel={handleClose} />
          </>
        )}
      </div>
    </Modal>
  );
};
